import type { WritableComputedRef } from 'vue';
import type { AnyItem, CompositePrimaryKey, RelationOperationToOne } from '#layers/data-model';
import type { ResolvedMonospaceSingleRelationField } from '#layers/data-model/types/schema/resolved-schema';
import type { FormBlockContext } from '~/composables/form-block/types';
import { toValue } from 'vue';
import { getRelationPrimitiveFields, isConnectToOne, nestedToOneConnect } from '#layers/data-model';
import { getItemPrimaryKey, isSameKey } from '#layers/data-model/utils/primary-key';
import {
	createdItemKey,
	fromReferencedItemKey,
	verifyItemKeys,
} from '~/composables/form-block/use-relation-field-state';

export function createRelationFieldStateToOneInCreateHandler(field: ResolvedMonospaceSingleRelationField, fieldValue: WritableComputedRef<RelationOperationToOne | undefined, RelationOperationToOne | undefined>, formBlockContext: FormBlockContext | null) {
	function handleCreate(payload: AnyItem) {
		fieldValue.value = { _create: { data: { ...payload, [createdItemKey]: crypto.randomUUID() } } };
		const { fields: pkFields } = getRelationPrimitiveFields(field);
		const formState = formBlockContext?.state.value;

		if (!formState) return;

		for (const field of pkFields) {
			formState[field.apiName] = null;
		}
	}

	function handleConnect(key: CompositePrimaryKey) {
		fieldValue.value = nestedToOneConnect({ key, mode: 'create' });

		const primitiveValues = fromReferencedItemKey(key, field);
		const formState = formBlockContext?.state.value;

		if (!primitiveValues || !formState) return;

		for (const [fieldKey, value] of Object.entries(primitiveValues)) {
			formState[fieldKey] = value;
		}
	}

	function handleDisconnect(key: CompositePrimaryKey) {
		fieldValue.value = undefined;

		// make sure the primitive fields on this item are set to `null`
		const primitiveValues = fromReferencedItemKey(key, field);
		const formState = formBlockContext?.state.value;

		if (!primitiveValues || !formState) return;

		for (const fieldKey of Object.keys(primitiveValues)) {
			formState[fieldKey] = null;
		}
	}

	function handleUpdate(originalKey: CompositePrimaryKey | null, payload: AnyItem) {
		const isNewlyConnectedItem = isConnectToOne(fieldValue.value!) && isSameKey(fieldValue.value!._connect.key, getItemPrimaryKey(payload, toValue(field).oppositeCollection!));
		originalKey ??= getItemPrimaryKey(payload, toValue(field).oppositeCollection!);
		verifyItemKeys(originalKey, payload, toValue(field).oppositeCollection!);

		if (!isNewlyConnectedItem) {
			fieldValue.value = { _create: { data: { ...payload, [createdItemKey]: crypto.randomUUID() } } };
		}
		else {
			throw new Error('Update after connect is currently not supposed.');
		}
	}

	return {
		handleConnect,
		handleDisconnect,
		handleCreate,
		handleUpdate,
	};
}
